/**
 * Combinatory logic simulator
 */
'use strict';

import { Tokenizer, restrict } from './internal';
import {
  Expr, FreeVar, Lambda, Church, Alias, Primitive, Named, Invocation, RefinedFormatOptions, toposort,
  Atomic,
} from './expr';

class Empty extends Expr {
  apply (...args: Expr[]):Expr {
    return args.length > 0 ? args.shift()!.apply(...args) : this;
  }

  postParse (): Expr {
    throw new Error('Attempt to use empty expression () as a term');
  }

  formatImpl (options: RefinedFormatOptions, nargs: number): string {
    return '()';
  }
}

class PartialLambda extends Empty {
  impl: Expr;
  terms: FreeVar[];
  // TODO mutable! rewrite ro when have time
  constructor (term: Expr, _known = {}) {
    super();
    this.impl = new Empty();
    if (term instanceof FreeVar)
      this.terms = [term];
    else if (term instanceof PartialLambda) {
      if (!(term.impl instanceof FreeVar))
        throw new Error('Expected FreeVar->...->FreeVar->Expr');
      this.terms = [...term.terms, term.impl];
    } else
      throw new Error('Expected FreeVar or PartialLambda');
  }

  apply (term: Expr, ...tail:Expr[]) {
    if (term === null || tail.length !== 0 )
      throw new Error('bad syntax in partial lambda expr');
    this.impl = this.impl.apply(term);
    return this;
  }

  postParse (): Expr {
    let expr = postParse(this.impl);
    for (let i = this.terms.length; i-- > 0; )
      expr = new Lambda(this.terms[i], expr);
    return expr;
  }

  // uncomment if debugging with prints
  /* toString () {
    return this.terms.join('->') + '->' + (this.impl ?? '???');
  } */
}

function postParse (expr: Expr): Expr {
  return expr instanceof Empty ? expr.postParse() : expr;
}

const combChars = new Tokenizer(
  '[()]', '[A-Z]', '[a-z_][a-z_0-9]*', '\\b[0-9]+\\b', '->', '\\+'
);

// declare builtin combinators, available in all interpreters by default
// at least 'K', 'S', 'I', and '+' should be available for number parsing and SKI.extras.toSKI to work
export const builtin: Record<string, Primitive> = {};
function addNative (name: string, impl: (arg: Expr) => Invocation, opt : {note?: string} = {}): void {
  builtin[name] = new Primitive(name, impl, opt);
}
addNative('I', x => x);
addNative('K', x => _y => x);
addNative('S', x => y => z => x.apply(z, y.apply(z)));
addNative('B', x => y => z => x.apply(y.apply(z)));
addNative('C', x => y => z => x.apply(z).apply(y));
addNative('W', x => y => x.apply(y).apply(y));

addNative(
  '+',
  n => n instanceof Church
    ? new Church(n.n + 1)
    : f => x => f.apply(n.apply(f, x)),
  {
    note: 'Increase a Church numeral argument by 1, otherwise n => f => x => f(n f x)',
  }
);

export type ParseOptions = {
  env?: { [key: string]: Expr },
  scope?: object,
  numbers?: boolean,
  lambdas?: boolean,
  atomic?: boolean,
  experimental?: boolean,
  allow?: string,
  canonize?: boolean,
  annotate?: boolean,
  addContext?: boolean,
};

// Engine-wide defaults for ParseOptions, plus a one-off list of known terms
// to pre-populate the interpreter with (see bulkAdd() for the accepted syntax).
export type EngineOptions = ParseOptions & {
  terms?: { [key: string]: Expr | string } | string[],
};

export type AddOptions = {
  note?: string,
  canonize?: boolean,
  fancy?: string,
  arity?: number,
};

export class Parser {
  /**
   *
   * @param {EngineOptions} [options]
   * @param {string} [options.allow]
   * @param {boolean} [options.numbers]
   * @param {boolean} [options.lambdas]
   * @param {boolean} [options.atomic]
   * @param {boolean} [options.experimental]
   * @param {boolean} [options.annotate]
   * @param {boolean} [options.addContext]
   * @param {{ [key: string]: Expr|string} | string[]} [options.terms]
   */

  known: Record<string, Named>;
  allow: Set<string>;

  // Engine-wide defaults for ParseOptions, applied whenever a per-call
  // option is not explicitly given.
  options: Required<Omit<ParseOptions, 'env' | 'scope' | 'allow' | 'canonize'>>;

  constructor (options: EngineOptions = {}) {
    const { terms, allow, ...defaults } = options;

    this.known = { ...builtin };

    this.options = {
      annotate:     !!defaults.annotate,
      addContext:   !!defaults.addContext,
      // Relax permissions before importing terms, restrict later
      numbers:      true,
      lambdas:      true,
      atomic:       true,
      experimental: true,
    };
    /** @type {Set<string>} */
    this.allow = new Set(Object.keys(this.known));

    // Import terms, if any. Omit native ones
    if (Array.isArray(terms))
      this.bulkAdd(terms);
    else if (terms) {
      for (const name in terms) {
        // Primitive terms already handled by allow
        if (typeof terms[name] !== 'string' || !terms[name].match(/^Primitive:/))
          this.add(name, terms[name]);
      }
    }

    // Finally, impose restrictions
    // We must do it after recreating terms, or else terms reliant on forbidden terms will fail
    this.options.numbers      = defaults.numbers ?? true;
    this.options.lambdas      = defaults.lambdas ?? true;
    this.options.atomic       = defaults.atomic ?? false;
    this.options.experimental = !!defaults.experimental;

    if (allow)
      this.restrict(allow);
  }

  /**
   *  Declare a new term
   * If the first argument is an Alias, it is added as is.
   * Otherwise, a new Alias or Primitive term (depending on impl type) is created.
   * If note is not provided and this.options.annotate is true, an automatic note is generated.
   *
   * If impl is a function, it should have signature (Expr) => ... => Expr
   * (see typedef Partial at top of expr.js)
   *
   * @example ski.add('T', 'S(K(SI))K', 'swap combinator')
   * @example ski.add( ski.parse('T = S(K(SI))K') ) // ditto but one-arg form
   * @example ski.add('T', x => y => y.apply(x), 'swap combinator') // heavy artillery
   * @example ski.add('Y', function (f) { return f.apply(this.apply(f)); }, 'Y combinator')
   *
   * @param {Alias|String} term
   * @param {String|Expr|function(Expr):Partial} [impl]
   * @param {object|string} [options]
   * @param {string} [options.note] - optional annotation for the term, default is auto-generated if this.options.annotate is true
   * @param {boolean} [options.canonize] - whether to canonize the term's implementation, default is this.options.annotate
   * @param {boolean} [options.fancy] - alternative HTML-friendly name for the term
   * @param {number} [options.arity] - custom arity for the term, default is inferred from the implementation
   * @return {SKI} chainable
   */
  add (term: Named | string, impl?: Expr | string | ((arg: Expr) => Invocation), options?: AddOptions | string): this {
    const named = this._named(term, impl);

    // backward compat
    const opts: AddOptions = typeof options === 'string' ? { note: options, canonize: false } : (options ?? {});
    named.annotate({ canonize: this.options.annotate, ...opts });

    const old = this.known[named.name];
    if (old instanceof Alias)
      old.makeInline();

    this.known[named.name] = named;
    this.allow.add(named.name);

    return this;
  }

  /**
   *  Internal helper for add() that creates an Alias or Primitive term from the given arguments.
   * @param {Alias|string} term
   * @param {string|Expr|function(Expr):Partial} impl
   * @returns {Primitive|Alias}
   * @private
   */
  _named (term: Named | string, impl?: Expr | string | ((arg: Expr) => Invocation)): Primitive | Alias {
    if (term instanceof Named)
      return maybeAlias(term.name, term);
    if (typeof term !== 'string')
      throw new Error('add(): term must be an Alias or a string');
    if (impl === undefined)
      throw new Error('add(): impl must be provided when term is a string');
    if (typeof impl === 'string')
      return maybeAlias(term, this.parse(impl));
    if (impl instanceof Expr)
      return maybeAlias(term, impl);
    if (typeof impl === 'function')
      return new Primitive(term, impl);
    // idk what this is
    throw new Error('add(): impl must be an Expr, a string, or a function with a signature Expr => ... => Expr');
  }

  /**
   *  Declare a new term if it is not known, otherwise just allow it.
   *       Currently only used by quests.
   *       Use with caution, this function may change its signature, behavior, or even be removed in the future.
   *
   * @experimental
   * @param {string|Alias} name
   * @param {string|Expr|function(Expr):Partial} impl
   * @returns {SKI}
   */
  maybeAdd (name: string, impl: Expr | string | ((arg: Expr) => Invocation)): this {
    if (this.known[name])
      this.allow.add(name);
    else
      this.add(name, impl);
    return this;
  }

  /**
   *  Declare and remove multiple terms at once
   *       term=impl adds term
   *       term= removes term
   * @param {string[]} list
   * @return {SKI} chainable
   */
  bulkAdd (list: string[]): this {
    for (const item of list) {
      const removeMatch = item.match(/^\s*([A-Z]|[a-z][a-z_0-9]*)\s*=\s*$/s);
      if (removeMatch) {
        this.remove(removeMatch[1]);
        continue;
      }
      if (item.match(/^\s*([A-Z]|[a-z][a-z_0-9]*)\s*$/s)) {
        this.allow.add(item.trim());
        continue;
      }
      const term = this.parse(item);
      if (!(term instanceof Named))
        throw new Error('bulkAdd: expected named term, got ' + term);
      this.add(term.name, term);
    }

    return this;
  }

  /**
   * Restrict the interpreter to given terms. Terms prepended with '+' will be added
   * and terms preceeded with '-' will be removed.
   * @example ski.restrict('SK') // use the basis
   * @example ski.restrict('+I') // allow I now
   * @example ski.restrict('-SKI +BCKW' ); // switch basis
   * @example ski.restrict('-foo -bar'); // forbid some user functions
   * @param {string} spec
   * @return {SKI} chainable
   */
  restrict (spec: string): this {
    this.allow = restrict(this.allow, spec);
    return this;
  }

  /**
   *
   * @param {string} spec
   * @return {string}
   */
  showRestrict (spec = '+') {
    const out = [];
    let prevShort: boolean = true;
    for (const term of [...restrict(this.allow, spec)].sort()) {
      const nextShort = !!term.match(/^[A-Z]$/);
      if (out.length && !(prevShort && nextShort))
        out.push(' ');
      out.push(term);
      prevShort = nextShort;
    }
    return out.join('');
  }

  /**
   *
   * @param {String} name
   * @return {SKI}
   */
  remove (name: string): this {
    const old = this.known[name];
    if (old instanceof Alias)
      old.makeInline();
    delete this.known[name];
    this.allow.delete(name);
    return this;
  }

  /**
   *
   * @return {{[key:string]: Primitive|Alias}}
   */
  getTerms (): Record<string, Named> {
    const out: Record<string, Named> = {};
    for (const name of Object.keys(this.known)) {
      if (this.allow.has(name))
        out[name] = this.known[name];
    }
    return out;
  }

  /**
   * Export term declarations for use in bulkAdd().
   * Alias'es and atomic (PureNative) terms are serialized.
   * @returns {string[]}
   */
  declare (): string[] {
    // TODO accept argument to declare specific terms only
    const env: { [key: string]: Named } = {};
    for (const [name, term] of Object.entries(this.getTerms())) {
      if (term instanceof Alias || term instanceof Atomic)
        env[name] = term;
    }

    // avert conflicts if native terms were redefined:
    // create a temporary alias for each native term that was redefined;
    // replace usage of redefined term in subexpressions;
    // finally, remove the temporary aliases from the output
    const needDetour: { [key: string]: Alias } = {};
    let i = 1;
    for (const name in builtin) {
      if (!(env[name] instanceof Alias))
        continue;
      while ('tmp' + i in env)
        i++;
      const original = env[name] as Alias;
      const temp = new Alias('tmp' + i, original.impl);
      needDetour[temp.name] = original;
      env[temp.name] = temp;
      delete env[name];
    }

    // console.log(env);

    const list = toposort({ list: Object.values(env), allow: {} }).list;

    const detour = new Map<Alias, Alias>();
    if (Object.keys(needDetour).length) {
      // replace aliases with their detoured counterparts.
      // we have to go recursive, otherwise an unrelated alias may be expanded to its impl
      // and name infos will be erased
      const rework = (expr: Expr): Expr => {
        return expr.traverse((e: Expr) => {
          if (!(e instanceof Alias))
            return null; // continue
          const newAlias = detour.get(e);
          if (newAlias)
            return newAlias;
          return new Alias(e.name, rework(e.impl));
        }) ?? expr;
      };

      for (let j = 0; j < list.length; j++) {
        // upon processing list[j], only terms declared before it may be detoured
        list[j] = rework(list[j]) as Named;
        const original = needDetour[(list[j] as Alias).name];
        if (original)
          detour.set(original, list[j] as Alias);
        env[(list[j] as Alias).name] = list[j] as Alias;
        // console.log(`list[${j}] = ${(list[j] as Alias).name}=${(list[j] as Alias).impl};`);
      }
      // console.log('detour:', detour);
    }

    // console.log(res);
    const out = list.map(e => e.declareImpl({ inventory: env, declaration: ['', '=', ''] }));

    for (const [name, temp] of detour)
      out.push((name as Alias).name + '=' + temp, temp + '=');

    return out;
  }

  /**
   * @param {string} source
   * @param {ParseOptions} [options]
   * @param [options.env] - additional
   * @param [options.scope] - assign this scope to unknown free variables
   * @param {boolean} [options.numbers] - whether numbers are allowed, default is set by engine options
   * @param {boolean} [options.lambdas] - whether lambdas are allowed, default is set by engine options
   * @param {boolean} [options.atomic] - whether \@atomic term definitions are allowed, default is set by engine options
   * @param {boolean} [options.experimental] - whether experimental features are allowed, default is set by engine options
   * @param {string} [options.allow] - restrict known terms
   * @param [options.canonize] - whether to calculate canonical form, arity, and properties
   *                             of intermediate aliases
   * @param {boolean} [options.addContext] - whether to attach parsing context to the result,
   *                                          default is set by engine options
   * @return {Expr}
   */
  parse (source: string, options: ParseOptions = {}): Expr {
    // yes this does happen
    if (typeof source !== 'string')
      throw new Error('parse: source must be a string, got ' + typeof source);

    const { jar, list } = this._parse(source, options);
    if (list.length === 0)
      throw new Error('parse: empty expression found in source');
    let expr = list[list.length - 1];

    if (options.addContext ?? this.options.addContext) {
      // create a transparent alias to avoid mutating the original term
      if (expr instanceof Named)
        expr = new Alias(expr.name, expr, { inline: true });
      expr.context = {
        env:    { ...this.getTerms(), ...jar }, // also contains pre-parsed terms
        scope:  options.scope,
        src:    source,
        parser: this,
      };
    }

    return expr;
  }

  include (source: string, options: ParseOptions = {}): Named[] {
    const { list } = this._parse(source, options);
    return list.filter(e => e instanceof Named);
  }

  _parse (source: string, options: ParseOptions = {}): { list: Expr[], jar: Record<string, Expr> } {
    const lines = source.replace(/\/\/[^\n]*$/gm, ' ')
      .replace(/\/\*.*?\*\//gs, ' ')
      .trim()
      .split(/\s*;[\s;]*/).filter( s => s.match(/\S/));

    const jar = { ...options.env };
    const list: Expr[] = [];

    for (const item of lines) {
      const expr = this.parseLine(item, jar, options);
      if (expr instanceof Named)
        jar[expr.name] = expr;
      // console.log('parsed line:', item, '; got:', expr,'; jar now: ', jar);
      list.push(expr);
    }

    return { list, jar };
  }

  /**
   *  Parse a single line of source code, without splitting it into declarations.
   *       Internal, always use parse() instead.
   * @template T
   * @param {String} source S(KI)I
   * @param {{[keys: string]: Expr}} env
   * @param {Object} [options]
   * @param {{[keys: string]: Expr}} [options.env] - unused, see 'env' argument
   * @param {T} [options.scope]
   * @param {boolean} [options.numbers]
   * @param {boolean} [options.lambdas]
   * @param {string} [options.allow]
   * @return {Expr} parsed expression
   */
  parseLine (source: string, env: { [key: string]: Expr } = {}, options: ParseOptions = {}): Expr {
    const isDef = source.match(/^\s*(?:(@[a-z][-a-z_0-9]*)\s+)?([A-Z]|[a-z][a-z_0-9]*)\s*=\s*(.*)$/s);

    if (isDef)
      return this._parseDef(isDef, env, options);

    const opt = {
      numbers: options.numbers ?? this.options.numbers,
      lambdas: options.lambdas ?? this.options.lambdas,
      allow:   restrict(this.allow, options.allow),
    };
    // make sure '+' usage is in sync with numerals
    if (opt.numbers) opt.allow.add('+'); else opt.allow.delete('+');

    const tokens = combChars.split(source);

    const empty = new Empty();
    /** @type {Expr[]} */
    const stack: Expr[] = [empty];
    const context = options.scope || FreeVar.global; // default is global unbound vars

    // TODO each token should carry along its position in source
    for (const c of tokens) {
      // console.log("parseLine: found "+c+"; stack =", stack.join(", "));
      if (c === '(')
        stack.push(empty);
      else if (c === ')') {
        if (stack.length < 2)
          throw new Error('unbalanced input: extra closing parenthesis' + source);
        const x = postParse(stack.pop()!);
        const f = stack.pop()!;
        stack.push(f.apply(x));
      } else if (c === '->') {
        if (!opt.lambdas)
          throw new Error('Lambdas not supported, allow them explicitly');
        stack.push(new PartialLambda(stack.pop()!));
      } else if (c.match(/^[0-9]+$/)) {
        if (!opt.numbers)
          throw new Error('Church numbers not supported, allow them explicitly');
        const f = stack.pop()!;
        stack.push(f.apply(new Church(Number.parseInt(c))));
      } else {
        const f = stack.pop()!;
        if (!env[c] && this.known[c] && !opt.allow.has(c)) {
          throw new Error('Term \'' + c + '\' is not in the restricted set '
            + [...opt.allow].sort().join(' '));
        }
        // look in temp vars first, then in known terms, then fallback to creating free var
        const x = env[c] ?? this.known[c] ?? (env[c] = new FreeVar(c, context));
        stack.push(f.apply(x));
      }
    }

    if (stack.length !== 1) {
      throw new Error('unbalanced input: missing '
          + (stack.length - 1) + ' closing parenthesis:' + source);
    }

    return postParse(stack.pop()!);
  }

  _parseDef (parts: string[], env: Record<string, Expr>, options: ParseOptions): Expr {
    const [_, tag, name, source] = parts;
    if (tag === undefined) {
      return source.match(/\S/)
        ? new Alias(name, this.parseLine(source, env, options))
        : new FreeVar(name, options.scope ?? FreeVar.global);
    }

    if (tag === '@inline') {
      if (!(options.experimental ?? this.options.experimental))
        throw new Error('Please allow @inline annotation explicitly via {"experimental": true}');
      return new Alias(name, this.parseLine(source, env, options), { canonize: false, inline: true });
    }

    if (tag === '@atomic') {
      if (!(options.atomic ?? this.options.atomic) && !(options.experimental ?? this.options.experimental))
        throw new Error('Please allow atomic terms explicitly in the interpreter ("atomic": true)');
      const self = new FreeVar(name); // no scope, self-bound placeholder
      const impl = this.parseLine(source, { ...env, [name]: self }, options);
      if (!(impl instanceof Lambda))
        throw new Error('Primitive term must be defined via a lambda expression');
      return new Atomic(self, impl);
    }

    throw new Error(`Unsupported tag '${tag}'`);
  }

  toJSON () {
    return {
      version:  '2.12.1', // set to incremented package.json version whenever SKI serialization changes
      allow:    this.showRestrict('+'),
      numbers:  this.options.numbers,
      lambdas:  this.options.lambdas,
      annotate: this.options.annotate,
      atomic:   this.options.atomic,
      terms:    this.declare(),
    }
  }
}

function maybeAlias (name: string, impl: Expr): Named {
  // Remove unnecessary extra aliases with the same name
  while (impl instanceof Alias && impl.name === name)
    impl = impl.impl;
  if (impl instanceof Named && impl.name === name)
    return impl;
  return new Alias(name, impl, { canonize: true });
}
